import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  notificationService,
  type ListNotificationsParams,
} from '../../../../services/notificationService';
import { useCurrentWorkspaceId } from '../../../../hooks/useStoreSelectors';
import { useNotificationStore } from '../../../../stores/notificationStore';

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (workspaceId: string, params?: ListNotificationsParams) =>
    [...notificationKeys.lists(), workspaceId, params] as const,
  unreadCount: (workspaceId: string) =>
    [...notificationKeys.all, 'unread-count', workspaceId] as const,
  analytics: (workspaceId: string) =>
    [...notificationKeys.all, 'analytics', workspaceId] as const,
};

// ─── List Notifications ─────────────────────────────────────────────────────

export function useNotificationsList(params?: ListNotificationsParams) {
  const workspaceId = useCurrentWorkspaceId();

  return useQuery({
    queryKey: notificationKeys.list(workspaceId || '', params),
    queryFn: () => notificationService.list(workspaceId!, params),
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
}

// ─── Recent Notifications (for dropdown) ────────────────────────────────────

export function useRecentNotifications() {
  const workspaceId = useCurrentWorkspaceId();

  return useQuery({
    queryKey: notificationKeys.list(workspaceId || '', { limit: 10, page: 1 }),
    queryFn: () =>
      notificationService.list(workspaceId!, { limit: 10, page: 1 }),
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
}

// ─── Unread Count ───────────────────────────────────────────────────────────

export function useUnreadNotificationCount() {
  const workspaceId = useCurrentWorkspaceId();
  const setNotificationLoading = useNotificationStore(
    (state) => state.setLoading
  );

  return useQuery({
    queryKey: notificationKeys.unreadCount(workspaceId || ''),
    queryFn: async () => {
      setNotificationLoading(true);
      try {
        const result = await notificationService.getUnreadCount(workspaceId!);
        return result;
      } finally {
        setNotificationLoading(false);
      }
    },
    enabled: !!workspaceId,
    staleTime: 30_000,
    refetchInterval: 60_000, // Poll every 60s for unread count
  });
}

// ─── Mark As Read ───────────────────────────────────────────────────────────

export function useMarkNotificationAsRead() {
  const workspaceId = useCurrentWorkspaceId();
  const queryClient = useQueryClient();
  const storeMarkAsRead = useNotificationStore((state) => state.markAsRead);

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationService.markAsRead(workspaceId!, notificationId),
    onMutate: async (notificationId) => {
      // Optimistic update in store
      storeMarkAsRead(notificationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(workspaceId || ''),
      });
    },
  });
}

// ─── Mark All As Read ───────────────────────────────────────────────────────

export function useMarkAllNotificationsAsRead() {
  const workspaceId = useCurrentWorkspaceId();
  const queryClient = useQueryClient();
  const storeMarkAllAsRead = useNotificationStore(
    (state) => state.markAllAsRead
  );

  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(workspaceId!),
    onMutate: () => {
      // Optimistic update
      storeMarkAllAsRead();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(workspaceId || ''),
      });
    },
  });
}

// ─── Delete Notification ────────────────────────────────────────────────────

export function useDeleteNotification() {
  const workspaceId = useCurrentWorkspaceId();
  const queryClient = useQueryClient();
  const storeRemove = useNotificationStore(
    (state) => state.removeNotification
  );

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationService.delete(workspaceId!, notificationId),
    onMutate: async (notificationId) => {
      storeRemove(notificationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(workspaceId || ''),
      });
    },
  });
}

// ─── Clear All Notifications ────────────────────────────────────────────────

export function useClearAllNotifications() {
  const workspaceId = useCurrentWorkspaceId();
  const queryClient = useQueryClient();
  const storeClearAll = useNotificationStore((state) => state.clearAll);

  return useMutation({
    mutationFn: () => notificationService.clearAll(workspaceId!),
    onMutate: () => {
      storeClearAll();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(workspaceId || ''),
      });
    },
  });
}

// ─── Notification Analytics ─────────────────────────────────────────────────

export function useNotificationAnalytics(params?: {
  startDate?: string;
  endDate?: string;
  days?: number;
}) {
  const workspaceId = useCurrentWorkspaceId();

  return useQuery({
    queryKey: [...notificationKeys.analytics(workspaceId || ''), params],
    queryFn: () => notificationService.getAnalytics(workspaceId!, params),
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}